import { acceptedContactValues } from "../../../../../_shared/_logic/contact-comparison"
import { formatPropertyUnitLabel } from "../../../../../../../../../../../../../_shared/_logic/unit-labels"
import type {
    InboxWorkflowItem,
    InboxWorkflowRowOutcomeMap,
    InboxWorkflowState,
    InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
import {
  PROPERTY_CUSTOMER_ROLES,
  type PropertyCustomerRole,
} from "../../../../../../../../../../../../../_shared/_logic/enums"
import { formatInboxSourceLabel } from "../../../../../_shared/_logic/cells-presentation"
import type {
  AssignRequesterCustomerDecision,
  AssignRequesterCustomerPreviewPayload,
  AssignRequesterCustomerRole,
  AssignRequesterCustomerRow,
} from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {}

const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const roleValue = (...values: unknown[]): AssignRequesterCustomerRole => {
  const value = stringValue(...values).toUpperCase()

  return PROPERTY_CUSTOMER_ROLES.includes(value as PropertyCustomerRole)
    ? (value as AssignRequesterCustomerRole)
    : ""
}

const relevantItem = (
  item: InboxWorkflowItem,
  workflow: InboxWorkflowState,
  step: InboxWorkflowStep,
) => {
  if (item.action_case.flow_key !== workflow.flow.flow_key) return false

  if (item.steps.some((entry) => entry.step_key === step.step_key)) return true

  return step.requirements_resolved_by_step.some((requirement) =>
    item.action_case.requirements.includes(requirement),
  )
}

const itemNeedsStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) =>
  step.requirements_resolved_by_step.some(
    (requirement) =>
      item.action_case.requirements.includes(requirement) &&
      !item.action_case.resolved_requirements.includes(requirement),
  )

const itemResolvedStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = item.steps.find((entry) => entry.step_key === step.step_key)

  if (projected?.status === "resolved") return true

  if (projected?.status === "skipped") return false

  const requirements = step.requirements_resolved_by_step.filter((requirement) =>
    item.action_case.requirements.includes(requirement),
  )

  return (
    requirements.length > 0 &&
    requirements.every((requirement) =>
      item.action_case.resolved_requirements.includes(requirement),
    )
  )
}

const outputFor = (item: InboxWorkflowItem, stepKey: string) =>
  objectValue(item.steps.find((entry) => entry.step_key === stepKey)?.output)

const sourcePresentation = (payload: JsonObject, item: InboxWorkflowItem) => {
  const presentation = objectValue(payload.presentation)
  const origin = objectValue(payload.origin)

  return {
    sourceLabel: formatInboxSourceLabel(
      stringValue(
        presentation.source_label,
        origin.source,
        item.action_case.source,
        item.action_case.channel,
      ),
    ),
    sourceSubLabel: stringValue(
      presentation.source_subline,
      presentation.source_sub_label,
      "Terminrückmeldung",
    ),
  }
}

const propertyPresentation = (payload: JsonObject) => {
  const property = objectValue(payload.property)
  const address = objectValue(property.address)
  const unit = objectValue(property.unit)

  return {
    addressLabel: stringValue(address.label, address.freeform, property.label, "Liegenschaft"),
    unitLabel: formatPropertyUnitLabel(stringValue(unit.label, unit.hint, "Gebäudeeinheit")),
  }
}

const submittedContact = (payload: JsonObject) => {
  const requester = objectValue(payload.requester)
  const contact = objectValue(requester.contact)
  const suggestedContact = objectValue(requester.suggested_contact)

  return {
    name: stringValue(requester.submitted_name),
    email: stringValue(contact.email, suggestedContact.email),
    phone: stringValue(contact.phone, suggestedContact.phone),
    role: roleValue(requester.submitted_role, requester.suggested_role),
  }
}

type PersistedRequesterDecision = {
  decision: AssignRequesterCustomerDecision
  customerId: string
  displayName: string
  role: AssignRequesterCustomerRole
}

const persistedRequesterDecision = (output: JsonObject): PersistedRequesterDecision | null => {
  const decision = stringValue(output.decision)

  if (decision !== "use_existing" && decision !== "create_new") return null

  const customerId = stringValue(output.customer_id, output.requester_customer_id)
  const displayName = stringValue(output.display_name)

  if (!customerId || !displayName) return null

  return {
    decision,
    customerId,
    displayName,
    role: roleValue(output.property_customer_role),
  }
}

const durableOutputError = "Gespeicherte Ansprechpartner-Zuordnung fehlt oder ist ungültig."

const rowStatus = ({
  item,
  needsStep,
  resolved,
  skipped,
  previewReason,
  outputError,
  outcome,
}: {
  item: InboxWorkflowItem
  needsStep: boolean
  resolved: boolean
  skipped: boolean
  previewReason: string
  outputError: string | null
  outcome: InboxWorkflowRowOutcomeMap[string] | null
}) => {
  if (outcome?.status === "failed")
    return { label: "Prüfung nötig", detail: outcome.error || outcome.detail || null }

  if (outcome?.status === "skipped")
    return { label: "Übersprungen", detail: outcome.detail || null }

  if (outputError) return { label: "Datenfehler", detail: outputError }

  if (outcome?.status === "success" || resolved)
    return { label: "Erledigt", detail: outcome?.detail || null }

  if (skipped) return { label: "Übersprungen", detail: null }

  if (!item.compatible)
    return { label: "Nicht verfügbar", detail: item.error || "Dieser Fall ist nicht kompatibel." }

  if (previewReason) return { label: "Nicht verfügbar", detail: previewReason }

  return { label: "Offen", detail: null }
}

export const buildAssignRequesterCustomerRows = ({
  workflow,
  step,
  rowOutcomes,
  preview,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  rowOutcomes: InboxWorkflowRowOutcomeMap
  preview: AssignRequesterCustomerPreviewPayload | null
}): AssignRequesterCustomerRow[] =>
  workflow.items
    .filter((item) => relevantItem(item, workflow, step))
    .map((item) => {
      const actionCaseId = item.action_case.id
      const payload = objectValue(item.action_case.payload)
      const source = sourcePresentation(payload, item)
      const property = propertyPresentation(payload)
      const submitted = submittedContact(payload)
      const output = outputFor(item, "assign_requester_customer")
      const context = preview?.rowContextByActionCaseId[actionCaseId]
      const previewReason =
        preview?.rowErrorsByActionCaseId[actionCaseId] ||
        (!preview
          ? "Ansprechpartner-Vorschau fehlt."
          : !context
            ? "Ansprechpartner-Kontext fehlt."
            : "")
      const previewCustomerOptions = (context?.candidateCustomerIds ?? []).map((customerId) => ({
        ...preview!.customerCandidatesById[customerId],
        role: context!.candidateRolesByCustomerId[customerId],
      }))
      const needsStep = itemNeedsStep(item, step)
      const resolved = itemResolvedStep(item, step)
      const skipped =
        item.steps.find((entry) => entry.step_key === step.step_key)?.status === "skipped"
      const outcome = rowOutcomes[actionCaseId] ?? null
      const accepted = acceptedContactValues(output.contact_points)
      const persisted = persistedRequesterDecision(output)
      const outputError = resolved && !persisted ? durableOutputError : null
      const activePreviewReason = needsStep ? previewReason : ""
      const status = rowStatus({
        item,
        needsStep,
        resolved,
        skipped,
        previewReason: activePreviewReason,
        outputError,
        outcome,
      })
      const outputCustomerId = persisted?.customerId ?? null
      const selectedId = !needsStep
        ? outputCustomerId
        : (outputCustomerId ?? context?.selectedRequesterCustomerId ?? null)
      const durableCustomerOption =
        persisted?.decision === "use_existing" &&
        !previewCustomerOptions.some((candidate) => candidate.customerId === persisted.customerId)
          ? {
              customerId: persisted.customerId,
              displayName: persisted.displayName,
              role: persisted.role,
              email: "",
              phone: "",
            }
          : null
      const customerOptions = durableCustomerOption
        ? [...previewCustomerOptions, durableCustomerOption]
        : previewCustomerOptions
      const selected = selectedId
        ? (customerOptions.find((candidate) => candidate.customerId === selectedId) ?? null)
        : null
      const submittedName = context?.submittedName || submitted.name
      const suggestedRole = context?.submittedRole || submitted.role

      return {
        id: actionCaseId,
        actionCaseId,
        workflowItem: item,
        step: item.steps.find((entry) => entry.step_key === step.step_key) ?? null,
        payload: item.action_case.payload,
        ...source,
        ...property,
        propertyId: context?.propertyId ?? null,
        propertyUnitId: context?.propertyUnitId ?? null,
        submittedName,
        submittedEmail: submitted.email,
        submittedPhone: submitted.phone,
        suggestedRole,
        customerOptions,
        persistedEmail: accepted.email,
        persistedPhone: accepted.phone,
        persistedDecision: persisted?.decision ?? null,
        persistedCustomerId: selectedId,
        persistedDisplayName: !needsStep
          ? (persisted?.displayName ?? "")
          : stringValue(output.display_name, selected?.displayName),
        persistedRole: !needsStep
          ? (persisted?.role ?? "")
          : roleValue(output.property_customer_role, selected?.role),
        needsStep,
        rowOutcome: outcome,
        isOpen: needsStep,
        isResolved: resolved,
        isCompatible: item.compatible,
        disabled:
          outcome?.status === "success" ||
          !needsStep ||
          !item.compatible ||
          Boolean(activePreviewReason) ||
          Boolean(outputError),
        statusLabel: status.label,
        statusDetail: status.detail,
        statusHint: outputError || activePreviewReason || null,
        searchText: [
          item.action_case.title,
          source.sourceLabel,
          property.addressLabel,
          property.unitLabel,
          submittedName,
          submitted.email,
          submitted.phone,
          ...customerOptions.map((candidate) => candidate.displayName),
        ].join(" "),
      }
    })
